// ─── Astria India v2 — isolated English/Hindi/Tamil/Marathi language
// preference. Deliberately NOT the shared `preferredLanguage` localStorage
// key (that key is read by utils/getLanguage.ts's en/th/es whitelist and by
// several other lanes) — India v2 only ever supports these 4 languages,
// remembered on this device, never auto-switched, never mixed with any
// other lane's language state. Same pattern as
// components/astro/indonesiaV2/indonesiaV2Language.ts.
import { useCallback, useEffect, useState } from "react";

const INDIA_V2_LANGUAGE_KEY = "astriaIndiaV2Language";

export type IndiaV2Language = "en" | "hi" | "ta" | "mr";

const SUPPORTED_LANGUAGES: IndiaV2Language[] = ["en", "hi", "ta", "mr"];

export const getStoredIndiaV2Language = (): IndiaV2Language => {
  try {
    const value = localStorage.getItem(INDIA_V2_LANGUAGE_KEY);
    return (SUPPORTED_LANGUAGES as string[]).includes(value || "")
      ? (value as IndiaV2Language)
      : "en";
  } catch {
    return "en";
  }
};

export const setStoredIndiaV2Language = (lang: IndiaV2Language): void => {
  try {
    localStorage.setItem(INDIA_V2_LANGUAGE_KEY, lang);
  } catch {
    // ignore storage failures
  }
};

// React hook form — used by AstriaIndiaV2Shell + each tab so the shell's
// toggle and the tab body's copy re-render together from one source of
// truth, instead of the shell holding its own disconnected state.
export function useIndiaV2Language(): [
  IndiaV2Language,
  (lang: IndiaV2Language) => void,
] {
  const [lang, setLangState] = useState<IndiaV2Language>(
    getStoredIndiaV2Language,
  );

  useEffect(() => {
    try {
      localStorage.setItem(INDIA_V2_LANGUAGE_KEY, lang);
    } catch {
      // ignore
    }
  }, [lang]);

  const setLang = useCallback((next: IndiaV2Language) => {
    setLangState(next);
  }, []);

  return [lang, setLang];
}
